import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowRight, ChevronDown } from "lucide-react";
import { useStore } from "@/context/StoreContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1645451334440-41ae552c6f63?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxlbGVjdHJpYyUyMGJpa2UlMjB1cmJhbiUyMGNpdHklMjBjeWNsaW5nJTIwbmlnaHQlMjBvciUyMGR1c2t8ZW58MHx8fGJsYWNrfDE3OTEzNTYyMjJ8MA&ixlib=rb-4.1.0&q=85";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const ease = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const { openOrder } = useStore();
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 700], [0, 130]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.25]);

  return (
    <section className="relative min-h-screen grid-bg overflow-hidden flex items-center" data-testid="hero-section">
      <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-cyan-500/[0.07] blur-[130px]" />
      <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-[#FF4800]/[0.06] blur-[130px]" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20 grid lg:grid-cols-2 gap-14 lg:gap-8 items-center relative z-10 w-full">
        <motion.div style={{ opacity: fade }}>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease }}
            className="flex items-center gap-3 mb-7"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-radar absolute inline-flex h-full w-full rounded-full bg-cyan-400" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-400" />
            </span>
            <span className="mono text-[11px] sm:text-xs tracking-[0.3em] uppercase text-cyan-300/90" data-testid="hero-eyebrow">
              GPS-sporing · Plug &amp; Play · Norsk support
            </span>
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tight leading-[0.98] text-4xl sm:text-5xl lg:text-[4.4rem]" data-testid="hero-heading">
            <Line delay={0.3}>Elsykkelen din.</Line>
            <Line delay={0.45}>
              <span className="text-cyan-400 text-glow-cyan">Alltid innen</span>
            </Line>
            <Line delay={0.6}>rekkevidde.</Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease }}
            className="mt-7 text-base sm:text-lg text-slate-400 max-w-lg leading-relaxed"
            data-testid="hero-subtext"
          >
            Apexa Link monteres på elsykkelen. Se sykkelen live og få alarm ved bevegelse.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => openOrder()}
              data-testid="hero-order-button"
              className="group font-display font-bold bg-[#FF4800] hover:bg-[#ff5d1f] text-white px-8 py-4 rounded-full text-base transition-all duration-300 hover:shadow-[0_0_36px_rgba(255,72,0,0.5)] hover:-translate-y-0.5 flex items-center gap-2"
            >
              Bestill abonnement
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              href="#abonnement"
              onClick={(e) => {
                e.preventDefault();
                const el = document.querySelector("#abonnement");
                if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
              }}
              data-testid="hero-plans-link"
              className="font-display font-bold px-8 py-4 rounded-full text-base border border-white/15 text-slate-200 hover:border-cyan-400/50 hover:text-cyan-300 transition-all duration-300"
            >
              Se abonnement
            </a>
          </motion.div>

        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, ease }}
          className="relative"
        >
          <motion.div style={{ y: imgY }} className="relative">
            <div
              className="relative overflow-hidden rounded-3xl border border-white/10 shadow-[0_40px_120px_rgba(0,0,0,0.6)]"
              style={{ clipPath: "polygon(0 0, 100% 4%, 100% 100%, 0 96%)" }}
            >
              <img
                src={HERO_IMG}
                alt="Elsykkel i bylandskap om kvelden"
                className="w-full h-[420px] sm:h-[520px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#06090E]/85 via-transparent to-[#06090E]/20" />

              <div className="absolute top-[38%] left-[46%]">
                <span className="relative flex h-5 w-5">
                  <span className="animate-radar absolute inline-flex h-full w-full rounded-full border-2 border-cyan-400" />
                  <span className="animate-radar absolute inline-flex h-full w-full rounded-full bg-cyan-400/50" style={{ animationDelay: "0.6s" }} />
                  <span className="relative inline-flex rounded-full h-5 w-5 bg-cyan-400 border-2 border-[#06090E]" />
                </span>
              </div>
            </div>

            <div className="absolute -left-4 sm:-left-10 top-10 glass rounded-2xl border border-cyan-400/20 px-5 py-4 animate-float" data-testid="hero-card-position">
              <div className="flex items-center gap-3">
                <MapPin size={18} className="text-cyan-400" />
                <div>
                  <p className="mono text-[10px] tracking-[0.2em] text-slate-500 uppercase">Live posisjon</p>
                  <p className="text-sm font-semibold text-slate-100">Grünerløkka, Oslo</p>
                </div>
              </div>
            </div>

          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 text-slate-600"
      >
        <ChevronDown size={22} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
